import React from 'react';
import type { BinanceEquityCurvePoint } from './BinanceAlertShortLivePanel';

/**
 * 전체기간 자본 추이. 통계 칸 하나를 채우는 작은 라인차트다.
 *
 * **x축은 거래 순번**(point.n), 시간이 아니다 -- 거래가 뜸한 구간을 시간축으로
 * 그리면 존재하지 않는 구간이 평평한 선으로 보인다. 거래 단위면 그 구간은
 * 그림에서 그냥 없다, 이게 맞는 그림이다.
 *
 * Y축은 데이터 min~max 로 스케일한다(스파크라인처럼 진입가 대칭 고정을 쓰지
 * 않는다 -- 자본은 기준점이 없다).
 */
export function BinanceEquityCurveChart({
  curve,
}: {
  curve: BinanceEquityCurvePoint[] | undefined;
}): React.ReactElement | null {
  if (!curve || curve.length < 2) return null;

  const W = 100;
  const H = 30;
  const PAD_X = 2;

  const eqs = curve.map((p) => p.equity);
  let lo = Math.min(...eqs);
  let hi = Math.max(...eqs);
  if (lo === hi) { lo -= 1; hi += 1; }
  const pad = (hi - lo) * 0.1;
  lo -= pad;
  hi += pad;

  const nMax = curve.length - 1;
  const x = (i: number) => PAD_X + (i / nMax) * (W - 2 * PAD_X);
  const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;

  const d = curve.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(p.equity).toFixed(2)}`).join('');
  const first = curve[0].equity;
  const last = curve[curve.length - 1].equity;
  const color = last >= first ? '#3fb950' : '#f85149';

  return (
    <svg
      className="binance-equity-curve"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <path d={d} fill="none" stroke={color} strokeWidth={1.4} vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
